import { inject, Injectable } from '@angular/core';
import { LorenzFormControl, LorenzFormElement, LorenzFormGroup, LorenzFormList, LorenzFormService, ProcessStatusEnum } from '@lib/forms';
import { RequestOptions } from './request-options.interface';
import { AppIdEnum, ENVIRONMENT, MAINTENANCE_NOTIFICATION_SILENT_NETWORK_ERROR, ONE_TIME_TOKEN_REQUIRED, SPINNER_MESSAGE } from '@lib/shared';
import { RequestFormService } from './request-form/request-form';
import { catchError, finalize, map, Observable, of, switchMap, take, tap, throwError, timer } from 'rxjs';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Validators } from '@angular/forms';

@Injectable({
	providedIn: 'root'
})
export class LibRequestService {
	private readonly environment = inject(ENVIRONMENT);
	private readonly httpClient = inject(HttpClient);

	private readonly lorenzFormService = inject(LorenzFormService);
	private readonly requestFormService = inject(RequestFormService);

	private calculationInProgress = false;
	private calculationQueued = false;
	private readonly defaultEuCountryCodes = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'];

	/**
	 * Liefert ein Observable auf eine RequestForm mit Werten vom Server.
	 *
	 * @param requestOptions
	 */
	getRequestForm(requestOptions: RequestOptions): Observable<LorenzFormGroup | null> {
		if (!requestOptions?.requestId) {
			return of(null);
		}

		const url = this.environment.apiServer + '/api/v1/requests/show'; // Die Dialogdaten werden mitgeliefert!

		const data = {
			id: requestOptions.requestId,
			appId: requestOptions.appId
		};

		const context = new HttpContext().set(SPINNER_MESSAGE, 'Daten werden abgerufen');

		return this.httpClient.post<any>(url, data, { context }).pipe(
			map((response) => {
				if (!response.payload?.dialogData || !response.payload?.values) {
					return null;
				}

				requestOptions = {
					...requestOptions,
					dialogData: response.payload.dialogData
				};

				const values = response.payload.values;
				const requestForm = this.buildRequestForm(requestOptions, values);

				console.log('getRequestForm', requestForm);

				return requestForm;
			}),
			tap((form) => form?.markAllAsTouched()),
			catchError((error: HttpErrorResponse) => {
				console.error('getRequestForm - Error occurred:', error.message);
				return of(null);
			})
		);
	}

	/**
	 * Liefert ein Observable auf eine leere RequestForm.
	 *
	 * @param requestOptions
	 * @param message
	 */
	newRequestForm(requestOptions: RequestOptions, message: string): Observable<LorenzFormGroup | null> {
		// prettier-ignore
		return this.fetchDialogData(message)
			.pipe(
				take(1),
				map((response: any) => {
					console.log('newRequestForm - dialogData', response);
					return this.buildRequestForm({
						...requestOptions,
						dialogData: response.payload
					});
				}),
				catchError(err => {
					return of(null);
				})
			);
	}

	/**
	 * Liefert die RequestForm, ggf. mit Werten.
	 *
	 * @param requestOptions
	 * @param patchValues
	 */
	private buildRequestForm(requestOptions: RequestOptions, patchValues?: any): LorenzFormGroup {
		let functions: { [key: string]: Function } = {
			calculatePersonalPositions: this.calculatePersonalPositions,
			isKundennummerRequired: this.isKundennummerRequired,
			isVatRequired: this.isVatRequired,
			storeRequestData: this.storeRequestData,
			saveButtonFn: this.saveButtonFn,
			setLorenzMailAddress: this.setLorenzMailAddress
		};

		// Functions from the options.

		if (requestOptions.setLastMinuteHintFn instanceof Function) {
			functions = {
				...functions,
				setLastMinuteHintFn: requestOptions.setLastMinuteHintFn
			};
		}

		const requestForm = this.requestFormService.requestForm(requestOptions, functions);

		// Patch values (if provided).
		this.lorenzFormService.setValues(requestForm, patchValues);

		// Set the payload for the form.
		requestForm.payload = {
			...requestForm?.payload,
			...requestOptions
		};

		// Set the form state.

		const formStatus = patchValues?._lorenz?.status ?? ProcessStatusEnum.NEW;

		switch (formStatus) {
			case ProcessStatusEnum.NEW: {
				if (requestOptions.appId === AppIdEnum.REQUEST) {
					requestForm.setLocked(false);
					requestForm.payload.hasAddButton = true;
					requestForm.payload.hasDeleteButton = true;
					requestForm.payload.hasSaveButton = false;
				} else {
					requestForm.setLocked(false);
					requestForm.payload.hasAddButton = true;
					requestForm.payload.hasDeleteButton = true;
					requestForm.payload.hasSaveButton = true;
				}
				break;
			}

			case ProcessStatusEnum.REVIEW: {
				if (requestOptions.appId === AppIdEnum.REQUEST) {
					requestForm.setLocked(true);
					requestForm.payload.hasAddButton = false;
					requestForm.payload.hasDeleteButton = false;
					requestForm.payload.hasSaveButton = false;
				} else {
					requestForm.setLocked(false);
					requestForm.payload.hasAddButton = true;
					requestForm.payload.hasDeleteButton = true;
					requestForm.payload.hasSaveButton = true;
				}
				break;
			}

			case ProcessStatusEnum.YOUSIGN_LORENZ:
			case ProcessStatusEnum.YOUSIGN_PARTNER: {
				requestForm.setLocked(true);
				requestForm.payload.hasAddButton = false;
				requestForm.payload.hasDeleteButton = false;
				requestForm.payload.hasSaveButton = false;
				break;
			}

			case ProcessStatusEnum.CANCELED:
			case ProcessStatusEnum.DECLINED:
			case ProcessStatusEnum.SIGNED: {
				requestForm.setLocked(true);
				requestForm.payload.hasAddButton = false;
				requestForm.payload.hasDeleteButton = false;
				requestForm.payload.hasSaveButton = false;
				break;
			}
		}

		// Initialize the validations for all elements.
		this.lorenzFormService.initValidations(requestForm);

		// Initialize the value buffer for all elements.
		this.lorenzFormService.initValueBuffer(requestForm);

		// Set and initialize all dependencies.
		this.lorenzFormService.applyDependencies(requestForm, requestForm);

		// this.lorenzFormService.printFormStructure(requestForm); // only for DEBUG
		// console.log('requestForm', requestForm);

		// Purge the structure
		// this.lorenzFormService.purgeFormGroup(requestForm);

		requestForm.valueChanges.subscribe(() => this.lorenzFormService.updateLastUpdate(requestForm));

		requestForm.updateValueAndValidity();

		return requestForm;
	}

	// Holt die Dialogdaten, die wir für das Formular brauchen.
	private fetchDialogData($message: string): Observable<any> {
		const context = new HttpContext().set(SPINNER_MESSAGE, $message);
		return this.httpClient.post<any>(`${this.environment.apiServer}/api/v1/requests/new`, {}, { context });
	}

	// enableEmployee(beschaeftigt_laufend: boolean, regulaer_liste: LorenzFormList) {
	// 	if (beschaeftigt_laufend && regulaer_liste?.controls?.length < 1) {
	// 		regulaer_liste.addItem();
	// 	}
	// }

	// setRequestFormStatus(formGroup: LorenzFormGroup, status: RequestStatusEnum): void {
	// 	formGroup.get('_lorenz.status')?.patchValue(status, { emitEvent: false });
	// }

	// updateJsonDataVersion(formGroup: LorenzFormGroup): void {
	// 	formGroup.get('_lorenz.json_data_version')?.patchValue(this.environment.appArbeitsvertrag.jsonDataVersion, { emitEvent: false });
	// }

	// private updateLastUpdate(formGroup: LorenzFormElement): void {
	// 	formGroup.get('_lorenz.last_update')?.patchValue(formatSystemDate(), { emitEvent: false });
	// }

	// Provides all groups, lists and controls with common data.
	// private setInitialPayload(commonData: { [key: string]: any }, lorenzFormGroup: LorenzFormGroup) {
	// 	lorenzFormGroup.payload = {
	// 		...lorenzFormGroup?.payload,
	// 		...commonData
	// 	};
	// }

	// ----- Form Functions ----------------------------------------------------------------------------------------------

	// Berechnet die Personalpositionen.
	private calculatePersonalPositions = (_value: any, lorenzFormRootGroup: LorenzFormGroup, lorenzFormElement: LorenzFormElement): void => {
		// If a calculation has just been requested, do not start another one.
		if (this.calculationInProgress) {
			this.calculationQueued = true;
			return;
		}

		const personalPositionen = lorenzFormRootGroup.get('personal.positionen') as LorenzFormList;
		if (personalPositionen && this.everythingEnabled(personalPositionen) && personalPositionen.valid && Array.isArray(personalPositionen.value)) {
			this.calculationInProgress = true;
			this.calculationQueued = false;

			// Update IDs
			(personalPositionen.controls as LorenzFormGroup[]).forEach((lorenzFormGroup: LorenzFormGroup, index: number) => {
				lorenzFormGroup.get('id')?.setValue(index + 1);
			});

			const data = personalPositionen.value.map((position: any) => ({
				id: position.id,
				type: position.funktion,
				quantity: position.anzahl,
				date: position.einsatztag,
				begin: position.schichtstart,
				end: position.schichtende,
				stundensatz: position.stundensatz ?? null,
				dayBeforeBriefing: position.einweisung === 'vortag',
				dayBeforeBriefingStart: position.einweisung_vortag_start,
				dayBeforeBriefingEnd: position.einweisung_vortag_ende
			}));
			const ignoreMinimumBookingControl = lorenzFormRootGroup.get('personal.ignore_minimum_booking_period') as LorenzFormControl | null;
			const disableMinimumBookingPeriod = !!ignoreMinimumBookingControl?.value;
			const ignoreLastMinuteControl = lorenzFormRootGroup.get('personal.ignore_last_minute_surcharge') as LorenzFormControl | null;
			const disableLastMinuteSurcharge = !!ignoreLastMinuteControl?.value;
			const payload = {
				positions: data,
				disableMinimumBookingPeriod,
				disableLastMinuteSurcharge
			};

			const minimumBookingPeriodNotReachedText = (lorenzFormRootGroup.payload as any)?.minimumBookingPeriodNotReachedText ?? '';

			// prettier-ignore
			this.calculatePositions(payload)
				.pipe(
					finalize(() => {
						this.calculationInProgress = false;
						if (this.calculationQueued) {
							this.calculationQueued = false;
							this.calculatePersonalPositions(null, lorenzFormRootGroup, lorenzFormElement);
						}
					})
				)
				.subscribe({
					next: (response: any) => {
						response?.payload?.positions?.forEach((position: any) => {
							if (personalPositionen?.controls) {
								const matchingGroup = personalPositionen.controls.find(group =>
									group.get('id')?.value === position.id
								);

								if (matchingGroup) {
									matchingGroup.patchValue({

										template: position.template ?? null,

										stunden: position.work?.positionHours ?? null,
										stunden_marker: position.work?.MinimumBookingPeriodNotReached ?? null,
										preis: position.work?.positionPrice ?? null,
										buchungszeit_hinweis: !!position.work?.MinimumBookingPeriodNotReached ? minimumBookingPeriodNotReachedText : null,

										einweisung_vortag_stunden: position.briefing?.positionHours ?? null,
										einweisung_vortag_stunden_marker: position.briefing?.MinimumBookingPeriodNotReached ?? null,
										einweisung_vortag_preis: position.briefing?.positionPrice ?? null,

										einweisung_vortag_buchungszeit_hinweis: !!position.briefing?.MinimumBookingPeriodNotReached ? minimumBookingPeriodNotReachedText : null
									});
								}
							}
						});

						if (personalPositionen?.parent && response?.payload) {
							const summen = personalPositionen.parent.get('summen') as LorenzFormGroup;

							if (summen) {
								summen.patchValue({
									stunden: response.payload.totalHours,
									preise: response.payload.totalPrice,
									last_minute_zuschlag: !!response.payload.LastMinuteSurcharge,
									last_minute_text: response.payload.LastMinuteSurcharge ? 'LastMinuteSurcharge' : null
								});
							}
						}
					},
					error: (error) => {
						console.error('Position calculation failed', error);
					}
				});
		}
	};

	private calculatePositions(payload: unknown): Observable<any> {
		const url = `${this.environment.apiServer}/api/v1/requests/calculate`;
		const initialContext = new HttpContext()
			.set(SPINNER_MESSAGE, 'Positionen werden neu berechnet')
			.set(ONE_TIME_TOKEN_REQUIRED, true)
			.set(MAINTENANCE_NOTIFICATION_SILENT_NETWORK_ERROR, true);
		const retryContext = new HttpContext()
			.set(SPINNER_MESSAGE, 'Positionen werden neu berechnet')
			.set(ONE_TIME_TOKEN_REQUIRED, true);

		return this.httpClient.post(url, payload, { context: initialContext }).pipe(
			catchError((error: HttpErrorResponse) => {
				if (error.status !== 0) {
					return throwError(() => error);
				}

				// A status-0 response has no usable server response and can also leave the
				// one-time token out of sync. Refresh it before retrying this idempotent
				// calculation once. The retry itself uses the normal global diagnostics.
				return timer(750).pipe(
					switchMap(() => this.fetchDialogData('Verbindung wird wiederhergestellt')),
					switchMap(() => this.httpClient.post(url, payload, { context: retryContext }))
				);
			})
		);
	}

	// Prüft, ob alle Positionen einen Dienstvertrag erfordern. Wenn nicht, wird die Kundennummer 'required'.
	private isKundennummerRequired = (lorenzFormElementValue: any[], rootGroup: LorenzFormGroup, lorenzFormControl: LorenzFormControl): void => {
		if (lorenzFormElementValue && rootGroup.getAppId() === AppIdEnum.BACKEND_REQUEST_EDIT) {
			const hatNichtDienstvertrag = lorenzFormElementValue.some((position: any) => {
				return position.template !== 'dienst';
			});

			const kundennummerControl = rootGroup.getControl('aussteller.kundennummer');

			if (kundennummerControl) {
				if (hatNichtDienstvertrag) {
					kundennummerControl.setValidators([Validators.required]);
				} else {
					kundennummerControl.removeValidators(Validators.required);
				}
				kundennummerControl.updateValueAndValidity({ emitEvent: true });
			}
		}
	};

	// Prüft, ob die USt-IdNr. erforderlich ist und setzt die Validierung.
	private isVatRequired = (lorenzFormElementValue: any, rootGroup: LorenzFormGroup, lorenzFormControl: LorenzFormControl): void => {
		const vatControl = lorenzFormControl?.parent?.get('ustId') as LorenzFormControl;
		if (!vatControl) {
			return;
		}

		const euCountryCodes = this.getEuCountryCodes(rootGroup).filter((code) => code !== 'DE');
		const countryCode = lorenzFormElementValue?.toUpperCase() ?? '';
		const required = euCountryCodes.includes(countryCode);

		vatControl.setRequired(required);
	};

	private getEuCountryCodes(rootGroup: LorenzFormGroup): string[] {
		const raw = (rootGroup?.payload as any)?.dialogData?.eu_country_codes;
		if (!Array.isArray(raw)) {
			return this.defaultEuCountryCodes;
		}

		const normalized = raw
			.map((value: any) =>
				String(value ?? '')
					.trim()
					.toUpperCase()
			)
			.filter((value: string) => value !== '');

		return normalized.length ? Array.from(new Set(normalized)) : this.defaultEuCountryCodes;
	}

	// Sendet die Request-Daten zum Speichern an den Server.
	public storeRequestData = (lorenzGroupFormRootValue: any, message: string, requestId?: string) => {
		const url = this.environment.apiServer + '/api/v1/requests/store';

		const context = new HttpContext()
			.set(SPINNER_MESSAGE, message)
			.set(ONE_TIME_TOKEN_REQUIRED, true);

		const data = {
			id: requestId,
			value: lorenzGroupFormRootValue
		}; // Cleanup is in the backend!

		return this.httpClient.post<any>(url, data, { context }).pipe(
			map((response) => {
				console.log('storeRequestData SUCCESS', response);
				return true;
			}),
			catchError((error: HttpErrorResponse) => {
				console.log('storeRequestData FAILED', error);
				return of(false);
			})
		);
	};

	// Kopiert eine komplette Messeanfrage und speichert sie als neuen Vorgang.
	public copyRequest(message: string, id: string) {
		const url = this.environment.apiServer + '/api/v1/requests/copy';

		const context = new HttpContext().set(SPINNER_MESSAGE, message);

		const data = {
			id
		};
		return this.httpClient.post<any>(url, data, { context }).pipe(
			map((response) => {
				console.log('copyRequest SUCCESS', response);
				return true;
			}),
			catchError((error: HttpErrorResponse) => {
				console.log('copyRequest FAILED', error);
				return of(false);
			})
		);
	}

	// Setzt die Mail-Adresse.
	public setLorenzMailAddress = (emailFromSelect: string, lorenzFormControl: LorenzFormControl, dependencyControl: LorenzFormControl): string => {
		const emailFromControl = lorenzFormControl?.value ?? emailFromSelect;
		const previousEmailFromSelect = dependencyControl.getPreviousValue() ?? dependencyControl.value;
		return previousEmailFromSelect.toLowerCase() === emailFromControl.toLowerCase() ? emailFromSelect : emailFromControl === '' ? emailFromSelect : emailFromControl;
	};

	// ----- TODO AUFRÄUMEN!!! -------------------------------------------------------------------------------------------

	public status(formGroup: LorenzFormGroup | null): ProcessStatusEnum {
		// prettier-ignore
		return formGroup
			? (formGroup.get('_lorenz.status')?.value as ProcessStatusEnum)
			: ProcessStatusEnum.NEW;
	}

	// Funktion für den Speichern-Button.
	public saveButtonFn = (lorenzGroupFormRoot: LorenzFormGroup, id: string | null = null, review = false) => {
		const requestId = (lorenzGroupFormRoot.payload as any).requestId; // TODO

		// prettier-ignore
		this.storeRequestFn(lorenzGroupFormRoot.getRawValue(), 'Eingaben werden gespeichert', requestId, review)
			.pipe(
				take(1)
			)
			.subscribe(() => {
					lorenzGroupFormRoot.markAsPristine();
					lorenzGroupFormRoot.markAsUntouched();
					lorenzGroupFormRoot.updateValueAndValidity();
				}
			);
	};

	// Liefert ein OBSERVABLE, dass die Contract-Daten zum Speichern an den Server sendet.
	public storeRequestFn = (lorenzGroupFormValue: any, message: string, id: string | null = null, review = false) => {
		const url = this.environment.apiServer + '/api/v1/requests/store';

		const context = new HttpContext().set(SPINNER_MESSAGE, message);

		const data = {
			id,
			value: lorenzGroupFormValue
		}; // Cleanup is in the backend!

		return this.httpClient.post<any>(url, data, { context }).pipe(
			map((response) => {
				console.log('storeRequestFn SUCCESS', response);
				return true;
			}),
			catchError((error: HttpErrorResponse) => {
				console.log('storeRequestFn FAILED', error);
				return of(false);
			})
		);
	};

	// ----- TODO In die Services verschieben ----------------------------------------------------------------------------

	// Prüft, ob alle Controls enabled sind.
	private everythingEnabled(lorenzFormList: LorenzFormList): boolean {
		if (!lorenzFormList) {
			return false;
		}
		return (lorenzFormList.controls as LorenzFormGroup[]).every((control: LorenzFormGroup) => control.isEnabled());
	}

	public getStatus(formGroup: LorenzFormGroup | null): ProcessStatusEnum {
		// prettier-ignore
		return formGroup
			? (formGroup.get('_lorenz.status')?.value as ProcessStatusEnum)
			: ProcessStatusEnum.NEW;
	}

	public addPermitted(lorenzFormList: LorenzFormList | null): boolean {
		if (!lorenzFormList) {
			return false;
		}
		return lorenzFormList.valid && lorenzFormList.isEnabled();
	}

	public deletePermitted(lorenzFormList: LorenzFormList | null): boolean {
		if (!lorenzFormList) {
			return false;
		}
		return lorenzFormList.length > 1 && lorenzFormList.isEnabled();
	}
}
